import { useState } from 'react';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';

type SelectInputProps = {
  className?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  options?: { name: string; value: string; element?: React.ReactNode }[];
  prefix?: string;
  small?: boolean;
  disabled?: boolean;
  emptyLabel?: string;
};

export default function SelectInput({
  className,
  placeholder,
  value,
  onChange,
  options,
  prefix,
  small,
  disabled,
  emptyLabel = 'No options',
}: SelectInputProps) {
  const [isOpen, setIsOpen] = useState(false);

  const displayedValue =
    options?.find((option) => option.value === value)?.name || '';

  function handleOptionClick(e) {
    const clickedVal = e.target.attributes['data-value']?.value;
    if (typeof onChange === 'function') {
      onChange(clickedVal);
    }
    setIsOpen((prevState) => !prevState);
  }

  return (
    <div className={`relative ${className ? className : ''}`}>
      <button
        className={`relative z-10 w-full`}
        type="button"
        onClick={() => {
          if (!disabled) {
            setIsOpen((prevState) => !prevState);
          }
        }}
      >
        <div
          className={`bg-white min-w-80 border-2 border-gray-300 text-left ${
            disabled ? 'bg-gray-100 cursor-default' : ''
          } ${
            isOpen
              ? `rounded-b-none border-b-0 ${
                  small
                    ? 'rounded-tl-half-small rounded-tr-half-small pb-2'
                    : 'rounded-tl-half rounded-tr-half pb-3.5'
                }`
              : 'rounded-full'
          } ${small ? 'px-5 py-1 text-sm' : 'py-3 px-8'}`}
        >
          {placeholder && (!value || !displayedValue) ? (
            <span className="text-gray-300">{placeholder}</span>
          ) : prefix ? (
            <span>
              {prefix}: <span className="font-bold">{displayedValue}</span>
            </span>
          ) : (
            displayedValue
          )}
        </div>
        <div
          className={`absolute right-5 text-xl inset-y-0 text-gray-600 flex items-center ${
            disabled ? 'cursor-default' : ''
          }`}
        >
          {isOpen ? <FaAngleUp /> : <FaAngleDown />}
        </div>
      </button>
      {isOpen && !disabled && (
        <button
          className="fixed inset-0 h-full w-full cursor-default z-0"
          tabIndex="-1"
          onClick={() => setIsOpen(false)}
        ></button>
      )}
      {isOpen && !disabled && (
        <div
          className={`absolute z-30 min-w-80 w-full max-h-56 overflow-auto bg-white border-2 border-t-0 border-gray-300 ${
            small
              ? 'rounded-bl-half-small rounded-br-half-small'
              : 'rounded-bl-half rounded-br-half'
          }`}
        >
          {(!options || options?.length === 0) && (
            <div className="w-full px-8 pb-3">{emptyLabel}</div>
          )}
          {options?.map(({ name, value, element }, idx) => {
            if (!element) {
              return (
                <button
                  key={value}
                  data-value={value}
                  onClick={handleOptionClick}
                  className={`block text-left w-full px-8 hover:bg-primary last:rounded-b-half focus:ring-primary focus-visible:ring-primary ${
                    small
                      ? 'py-0.5 text-sm last:rounded-b-half-small'
                      : 'py-3 last:rounded-b-half'
                  }`}
                >
                  {name}
                </button>
              );
            }
            return (
              <div
                key={idx}
                className={`block text-left w-full px-8 hover:bg-primary focus:ring-primary focus-visible:ring-primary ${
                  small
                    ? 'py-0.5 text-sm last:rounded-b-half-small'
                    : 'py-3 last:rounded-b-half'
                }`}
              >
                {element}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
